import {featureFilter} from '@maplibre/maplibre-gl-style-spec';
import type {LayerSpecification, StyleSpecification} from '@maplibre/maplibre-gl-style-spec';
import {wrapError} from '@northpoint-development/labs-common-core';

import {colorToArray} from '@/services/GraphicService/colorUtilities';
import {resolvePaintValue} from './paintValues';

import type {MVTFeature} from '@/services/MapService/MVTLayers/types';

/** A style layer next to a ready-made test for whether a feature belongs to it. */
interface StyleLayerMatcher {
  layer: LayerSpecification;
  matches: (globalProperties: {zoom: number}, feature: MVTFeature) => boolean;
}

/**
 * `featureFilter` compiles an expression tree, so it happens here once per stylesheet rather than once
 * per feature.
 *
 * When Esri packs a tile it adds a center point for every polygon, for labeling and for drawing small
 * polygons far out. So a tile of nothing but polygons still arrives full of points. ArcGIS Pro hides them
 * by drawing only the geometry a style layer asks for; deck.gl draws everything it is given.
 */
const readStyleLayers = (styleJson: StyleSpecification) => {
  const matchers = new Map<string, StyleLayerMatcher[]>();
  const allowedGeometryTypes = new Map<string, Set<string>>();
  for (const layer of styleJson.layers) {
    // Narrows the type: background is the one kind of style layer with no source-layer.
    if (layer.type === 'background') continue;
    const sourceLayer = layer['source-layer'];
    if (!sourceLayer) continue;

    let matches: StyleLayerMatcher['matches'];
    try {
      matches = featureFilter(layer.filter).filter as unknown as StyleLayerMatcher['matches'];
    } catch (e) {
      console.error(wrapError(e, `Style layer ${layer.id} has an invalid filter`));
      matches = () => true;
    }

    const matcher = {layer, matches};
    const existing = matchers.get(sourceLayer);
    if (existing) {
      existing.push(matcher);
    } else {
      matchers.set(sourceLayer, [matcher]);
    }

    let allowed = allowedGeometryTypes.get(sourceLayer);
    if (!allowed) {
      allowed = new Set<string>();
      allowedGeometryTypes.set(sourceLayer, allowed);
    }
    switch (layer.type) {
      case 'fill':
        allowed.add('Polygon');
        allowed.add('MultiPolygon');
        break;
      case 'line':
        allowed.add('LineString');
        allowed.add('MultiLineString');
        break;
      case 'circle':
      case 'symbol':
        allowed.add('Point');
        allowed.add('MultiPoint');
        break;
    }
  }
  return {matchers, allowedGeometryTypes};
};

/**
 * Returns a list, not one layer: a stylesheet can describe a single shape with several style layers, such as
 * a fill for the body and a line for its outline.
 */
const getMatchingStyleLayers = (
  matchers: Map<string, StyleLayerMatcher[]>, feature: MVTFeature,
): LayerSpecification[] => {
  const candidates = matchers.get(feature.properties?.layerName);
  if (!candidates) return [];
  return candidates.filter(({matches}) => matches({zoom: 0}, feature)).map(({layer}) => layer);
};

/**
 * Flattens every style layer that matches the feature into the one set of values deck.gl asks for. A shape
 * described by a fill layer and a line layer still has to come back as a single color, outline, and width.
 */
const paintFeature = (matchers: Map<string, StyleLayerMatcher[]>, feature: MVTFeature) => {
  const matchingLayers = getMatchingStyleLayers(matchers, feature);
  if (!matchingLayers.length) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getPropertyValue = (propertyConfig: unknown): any => resolvePaintValue(propertyConfig, feature.properties);

  // Each layer type has its own paint keys, so LayerSpecification refuses to be indexed by any one of them.
  const fillLayer = matchingLayers.find((l) => l.type === 'fill');
  const lineLayer = matchingLayers.find((l) => l.type === 'line');
  const circleLayer = matchingLayers.find((l) => l.type === 'circle');
  const symbolLayer = matchingLayers.find((l) => l.type === 'symbol');
  const primary = matchingLayers[0];
  const primaryPaint = (primary.paint ?? {}) as Record<string, unknown>;
  const fillPaint = fillLayer?.paint as Record<string, unknown> | undefined;
  const linePaint = lineLayer?.paint as Record<string, unknown> | undefined;
  const circlePaint = circleLayer?.paint as Record<string, unknown> | undefined;
  const symbolPaint = symbolLayer?.paint as Record<string, unknown> | undefined;
  const symbolLayout = symbolLayer?.layout as Record<string, unknown> | undefined;

  const color = getPropertyValue(
    circlePaint?.['circle-color'] ||
    fillPaint?.['fill-color'] ||
    linePaint?.['line-color'] ||
    primaryPaint['circle-color'] ||
    primaryPaint['fill-color'] ||
    primaryPaint['line-color'],
  );
  // A paired line layer holds the outline color the publisher set per shape, so it beats the fill layer's
  // one fixed outline color. Every candidate is resolved before one is picked, so a line color that comes
  // back empty falls through to the fill's outline instead of erasing it.
  const strokeColor = getPropertyValue(circlePaint?.['circle-stroke-color'])
    ?? getPropertyValue(linePaint?.['line-color'])
    ?? getPropertyValue(fillPaint?.['fill-outline-color'])
    ?? getPropertyValue(primaryPaint['circle-stroke-color'])
    ?? getPropertyValue(primaryPaint['fill-outline-color']);
  const textColor = symbolPaint ? getPropertyValue(symbolPaint['text-color']) : [0, 0, 0, 255];
  const primaryLinePaint = primary.type === 'line' ? primaryPaint : undefined;
  const widthPaint = linePaint ?? primaryLinePaint;
  const lineWidth = widthPaint ? getPropertyValue(widthPaint['line-width']) : null;

  return {
    'radius': circlePaint ? getPropertyValue(circlePaint['circle-radius']) : null,
    'color': color,
    'strokeColor': strokeColor,
    // The `*Array` forms are the same colors parsed into the RGBA number arrays deck.gl's accessors read.
    // Parsing here means each color is read once per feature and then reused on every redraw, instead of
    // three times per feature per redraw. deck.gl copies the values into its own buffers, so handing back
    // the same array each time is safe.
    //
    // The line accessor falls back to the fill color, so that fallback is resolved here too.
    'colorArray': colorToArray(color),
    'strokeColorArray': colorToArray(strokeColor || color),
    'textColorArray': colorToArray(textColor || [0, 0, 0, 255]),
    'width': lineWidth,
    // MapLibre style-spec names
    'text-color': textColor,
    'text-size': symbolLayout ? getPropertyValue(symbolLayout['text-size']) : 12,
    'text-field': symbolLayout ? getPropertyValue(symbolLayout['text-field']) : '',
    'text-font': symbolLayout ? getPropertyValue(symbolLayout['text-font']) : ['Open Sans Regular'],
    'text-optional': symbolLayout ? getPropertyValue(symbolLayout['text-optional']) : false,
  };
};

type PaintResult = ReturnType<typeof paintFeature>;

/** Everything a layer needs to draw a tile from its stylesheet. */
interface LayerStyle {
  // deck.gl types the feature it hands its accessors loosely, so the cast to MVTFeature happens inside
  // rather than at all four accessors.
  paint: (feature: object) => PaintResult;
  allowedGeometryTypes: Map<string, Set<string>>;
}

/**
 * Held against the style object rather than the layer id, so a replaced stylesheet gets a new entry and
 * nothing has to watch the style JSON for changes.
 */
const _layerStyles = new WeakMap<StyleSpecification, LayerStyle>();

const getLayerStyle = (styleJson: StyleSpecification): LayerStyle => {
  const cached = _layerStyles.get(styleJson);
  if (cached) return cached;

  const {matchers, allowedGeometryTypes} = readStyleLayers(styleJson);
  // deck.gl asks four separate accessors for the same feature, and asks again on every redraw, but a
  // feature's paint cannot change.
  const results = new WeakMap<object, PaintResult>();
  const style: LayerStyle = {
    allowedGeometryTypes,
    paint: (feature) => {
      let result = results.get(feature);
      // Checked against `undefined` rather than for a falsy value, because a feature that matches no style
      // layer is stored as `null` and that answer is worth keeping.
      if (result === undefined) {
        result = paintFeature(matchers, feature as MVTFeature);
        results.set(feature, result);
      }
      return result;
    },
  };
  _layerStyles.set(styleJson, style);
  return style;
};

export {
  getLayerStyle,
};
